/**
 * Interpolation and variant selection for copy.
 *
 * Deliberately tiny and dependency-free: the app has a few dozen strings, and
 * an ICU message-format library would cost more bytes than the copy it renders
 * on the kind of connection this product is built for.
 */

export type FormatVars = Record<string, string | number>

const PLACEHOLDER = /\{(\w+)\}/g

/**
 * Replaces `{name}` style placeholders. An unmatched placeholder is left in
 * place rather than blanked, so a missing variable is visible in review instead
 * of producing a sentence with a hole in it.
 */
export function format(template: string, vars?: FormatVars): string {
  if (!vars) return template
  return template.replace(PLACEHOLDER, (whole, key: string) => {
    const value = vars[key]
    return value === undefined ? whole : String(value)
  })
}

/**
 * Picks one phrasing from a set. Rotation is driven by an explicit counter
 * rather than randomness so that tests, and a user's second run of the same
 * flow, are both predictable.
 */
export function pickVariant(variants: readonly string[], turn: number): string {
  if (variants.length === 0) return ''
  const index = ((turn % variants.length) + variants.length) % variants.length
  return variants[index] as string
}

/**
 * Counts variant usage per key so `pickVariant` can advance without the caller
 * threading a number through every call site.
 */
export class VariantRotator {
  private readonly counts = new Map<string, number>()

  next(key: string, variants: readonly string[]): string {
    const turn = this.counts.get(key) ?? 0
    this.counts.set(key, turn + 1)
    return pickVariant(variants, turn)
  }

  reset(): void {
    this.counts.clear()
  }
}
